import React, { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import parse from "html-react-parser";
import PropTypes, { number } from "prop-types";
import ReactMarkdown from "react-markdown";

import styles from "./Collections.module.css";

import {
  getContentsByKey,
  getContentValueByKey
} from "../../../shared/utilities/common";
import CmsInlineEdit from "../../Cms/CmsInlineEdit";
import amplitudeAnalytics from "../../../shared/utilities/amplitude";

const DarkLeftArrowIcon = require("./icons/dark-left-arrow.png");
const LeftArrowIcon = require("./icons/left-arrow.png");
const DarkRightArrowIcon = require("./icons/dark-right-arrow.png");
const RightArrowIcon = require("./icons/right-arrow.png");

function CollectionSlider(props) {
  const { data, onChange, selectedIndex = 0, openDeliveryWidget } = props;
  const location = useLocation();
  const [currentIndex, setcurrentIndex] = useState(selectedIndex);
  
  // Refs for focus management
  const contentRef = useRef(null);
  const leftArrowRef = useRef(null);
  const rightArrowRef = useRef(null);

  // Track whether user triggered navigation (vs automatic/parent change)
  const userTriggeredRef = useRef(false);

  const focusOnContent = () => {
    // Move focus to the visible panel's first focusable element or the panel itself
    setTimeout(() => {
      if (contentRef.current) {
        const visiblePanel = contentRef.current.querySelector('[role="tabpanel"]:not([aria-hidden="true"])');
        if (visiblePanel) {
          const focusableElement = visiblePanel.querySelector('button:not([tabindex="-1"]), [tabindex="0"]');
          if (focusableElement) {
            focusableElement.focus();
          } else {
            visiblePanel.focus();
          }
        }
      }
    }, 150);
  };

  const prevSlide = () => {
    const isFirstSlide = currentIndex === 0;
    const newIndex = isFirstSlide ? data?.length - 1 : currentIndex - 1;
    userTriggeredRef.current = true;
    setcurrentIndex(newIndex);
    /* eslint-disable-next-line */
    onChange && onChange(newIndex);
  };

  const nextSlide = () => {
    const isLastSlide = currentIndex === data.length - 1;
    const newIndex = isLastSlide ? 0 : currentIndex + 1;
    userTriggeredRef.current = true;
    setcurrentIndex(newIndex);
    /* eslint-disable-next-line */
    onChange && onChange(newIndex);
  };

  // Handle focus management when content changes (only if user triggered)
  useEffect(() => {
    if (userTriggeredRef.current) {
      focusOnContent();
      userTriggeredRef.current = false;
    }
  }, [currentIndex]);

  useEffect(() => {
    setcurrentIndex(selectedIndex);
  }, [selectedIndex]);

  const setSlide = (items, index) => {
    return items?.map((item, i) => {
      const title = getContentValueByKey(item.children, "title");
      const heading = getContentValueByKey(item.children, "heading");
      const description = getContentValueByKey(item.children, "description");
      const image = getContentValueByKey(item.children, "image");
      const link = getContentsByKey(item.children, "link");
      const linkTitle = getContentValueByKey(link, "title");
      const linkUrl = getContentValueByKey(link, "url");

      // Create safe ID strings with fallbacks - use title for consistency with tab IDs
      // eslint-disable-next-line
      const safeTitle = typeof title === 'string' ? title : (typeof heading === 'string' ? heading : `slide-${i}`);
      const safeTitleId = safeTitle.replace(/\s+/g, '-').toLowerCase();
      const isVisible = i === index;

      return (
        <CmsInlineEdit type="list" cmsNode={item} showContent={false} showChilds key={i}>
          <div
            id={`panel-${i}-${safeTitleId}`}
            role="tabpanel"
            aria-label={`${safeTitle} content`}
            aria-labelledby={`tab-${i}-${safeTitleId}`}
            tabIndex={isVisible ? 0 : -1}
            aria-hidden={!isVisible}
            className={`rounded-2xl bg-center bg-cover duration-500 animate-slide
                ${isVisible ? "" : "hidden"}
              `}
          >
            <div className="lg:grid grid-flow-col gap-0 place-items-center lg:w-[96%] lg:[70%]">
              <div className="w-full lg:w-[545px] m-0">
                <ReactMarkdown className={`${styles.productImage} lg:w-[90%] w-3/4 m-auto max-h-[290px] lg:max-h-[400px]`}>
                  {image}
                </ReactMarkdown>
              </div>
              <div className="lg:px-[62px] px-4 text-center lg:text-left col-span-1 w-full lg:w-[545px] mt-9 mr-5">
                <h3 className="text-dark dark:text-white font-bold text-[28px] tracking-[-1px] font-congenialBlack transform uppercase">
                  {heading}
                </h3>
                <p className="text-dark dark:text-white font-montserratBold text-lg leading-6 tracking-[-0.1px] my-2">
                  {typeof description === 'string' && parse(description)}
                </p>
                {linkTitle && linkUrl ? (
                  <button
                    onClick={() => {
                      const element = [heading, linkTitle].filter(Boolean).join(" — ") || "Collections CTA";
                      amplitudeAnalytics.trackCmsMappedPageClick({
                        pathname: location.pathname,
                        contentSection: "Category Carousel",
                        contentElement: element,
                      });
                      openDeliveryWidget(linkUrl);
                    }}
                    tabIndex={0}
                    onKeyDown={e => {
                      if (e.key === "Enter") {
                        const element = [heading, linkTitle].filter(Boolean).join(" — ") || "Collections CTA";
                        amplitudeAnalytics.trackCmsMappedPageClick({
                          pathname: location.pathname,
                          contentSection: "Category Carousel",
                          contentElement: element,
                        });
                        openDeliveryWidget(linkUrl);
                      }
                    }}
                    type="button"
                    className="h-[52px] px-4 text-center font-montserratBold border border-solid border-dark dark:border-darkBorder bg-transparent  rounded-[54px]"
                  >
                    <p className="text-dark outline-none text-lg dark:text-white">
                      {linkTitle}
                    </p>
                  </button>
                ) : (
                  ""
                )}
              </div>
            </div>
          </div>
        </CmsInlineEdit>
      );
    });
  };

  return (
    <div 
      className="m-auto w-full px-4 lg:px-0 relative md:max-w-screen-desktop my-8"
      role="region"
      aria-label="Product Collections Carousel"
    >
      {/* ARIA live region to announce content changes */}
      <div 
        aria-live="polite" 
        aria-atomic="true" 
        className="sr-only"
      >
        {data && data[currentIndex] && `Showing ${typeof getContentValueByKey(data[currentIndex].children, "heading") === 'string' ? getContentValueByKey(data[currentIndex].children, "heading") : `slide ${currentIndex + 1}`} content`}
      </div>
      
      {/* Content area - moved to top for logical focus order */}
      <div ref={contentRef} tabIndex={-1}>
        {data && setSlide(data, currentIndex)}
      </div>

      {/* Left Arrow */}
      <button
        ref={leftArrowRef}
        type="button"
        aria-label="Previous slide"
        onClick={prevSlide}
        className="absolute !w-auto lg:top-[40%] top-[10%] -translate-x-0 traslate-y-[-50%] left-0 text-2xl rounded-full p-2 cursor-pointer transition-colors bg-transparent border-0"
      >
        <img
          src={LeftArrowIcon}
          alt=""
          aria-hidden="true"
          className="dark:hidden block"
        />
        <img
          src={DarkLeftArrowIcon}
          alt=""
          aria-hidden="true"
          className="dark:block hidden"
        />
      </button>

      {/* Right Arrow  */}
      <button
        ref={rightArrowRef}
        type="button"
        aria-label="Next slide"
        onClick={nextSlide}
        className="absolute !w-auto lg:top-[40%] top-[10%] -translate-x-0 traslate-y-[-50%] right-0 text-2xl rounded-full p-2 cursor-pointer transition-colors bg-transparent border-0"
      >
        <img
          src={RightArrowIcon}
          alt=""
          aria-hidden="true"
          className="dark:hidden block"
        />
        <img
          src={DarkRightArrowIcon}
          alt=""
          aria-hidden="true"
          className="dark:block hidden"
        />
      </button>
    </div>
  );
}

CollectionSlider.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  onChange: PropTypes.func,
  selectedIndex: number,
  openDeliveryWidget: PropTypes.func
};

CollectionSlider.defaultProps = {
  data: null,
  onChange: null,
  selectedIndex: 0,
  openDeliveryWidget: null
};

export default CollectionSlider;
